<script setup lang="ts">
import {
  computed,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type {
  IForgotPasswordEnterSchema,
} from '@modules/forgot-password/types';
import type {
  FormSchema,
} from '@modules/forms/components/VForm/types';
import {
  FORGOT_PASSWORD_DONE_ROUTE_NAME,
  TRANSLATION_GROUP_FORGOT_PASSWORD,
} from '@modules/forgot-password/constants';
import {
  useForgotPasswordEnterMutation,
} from '@modules/forgot-password/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { isString } from '@utils/guards';

const router = useRouter();
const route = useRoute();
const { getTranslation } = useI18n();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useForgotPasswordEnterMutation();

const routeToken = computed(() => (isString(route.params.token) ? route.params.token : ''));

const formSchema = computed<FormSchema<IForgotPasswordEnterSchema>>(() => ({
  password: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CHANGE_PASSWORD').value,
    required: true,
  },
  password_confirmation: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CHANGE_PASSWORD_CONFIRMATION').value,
    required: true,
  },
}));

async function onSubmit(values: IForgotPasswordEnterSchema): Promise<void> {
  await mutateAsync({
    ...values,
    reset_password_token: routeToken.value,
  });
  void router.replace({
    name: FORGOT_PASSWORD_DONE_ROUTE_NAME,
    params: {
      token: values.reset_password_token,
    },
  });
}
</script>

<template>
  <div v-data-qa-auto-id="'ForgotPasswordEnterPage'" class="input-max-width">
    <div class="text-center text-3xl font-light uppercase">
      {{ $getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CHANGE_HEADER') }}
    </div>
    <VForm
      :schema="formSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'CHANGE_SUBMIT')"
      is-buttons-centered
      :custom-errors="errors"
      :is-loading="isLoading"
      @interacted="clearError"
      @submit="onSubmit"
    />
  </div>
</template>
